'use client';

import { Eye, EyeOff, type LucideIcon } from 'lucide-react';
import Link from 'next/link';
import { useState, type InputHTMLAttributes, type ReactNode } from 'react';
import { inputClass } from '@/components/ui/primitives';
import { cn } from '@/lib/utils';

type IconInputProps = InputHTMLAttributes<HTMLInputElement> & { icon: LucideIcon; label: string; hint?: ReactNode; trailing?: ReactNode };

/** Tall auth input with a leading icon; the icon lights up while the field has focus. */
export function IconInput({ icon: Icon, label, hint, trailing, className, id, ...p }: IconInputProps) {
  const inputId = id ?? `auth-${p.name ?? label}`;
  return (
    <div>
      <label htmlFor={inputId} className="mb-1.5 block text-xs font-medium text-fg-2">
        {label}
      </label>
      <div className="group relative">
        <Icon className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-fg-3 transition-colors group-focus-within:text-lime" />
        <input id={inputId} className={cn(inputClass, 'h-12 rounded-xl pl-10 text-[15px]', trailing && 'pr-11', className)} {...p} />
        {trailing && <div className="absolute inset-y-0 right-1.5 flex items-center">{trailing}</div>}
      </div>
      {hint && <div className="mt-1.5 text-xs text-fg-3">{hint}</div>}
    </div>
  );
}

export function PasswordInput(props: Omit<IconInputProps, 'type' | 'trailing'>) {
  const [show, setShow] = useState(false);
  return (
    <IconInput
      {...props}
      type={show ? 'text' : 'password'}
      trailing={
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? 'Ocultar senha' : 'Mostrar senha'}
          className="flex size-9 items-center justify-center rounded-lg text-fg-3 transition-colors hover:bg-hover hover:text-fg"
        >
          {show ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
        </button>
      }
    />
  );
}

/** Entrar / Criar conta switch at the top of the auth card. */
export function AuthTabs({ active }: { active: 'login' | 'register' }) {
  const tabs = [
    { key: 'login', href: '/login', label: 'Entrar' },
    { key: 'register', href: '/cadastro', label: 'Criar conta' },
  ] as const;
  return (
    <div className="mb-7 grid grid-cols-2 gap-1 rounded-xl border border-border-soft bg-bg-2 p-1">
      {tabs.map((t) => (
        <Link
          key={t.key}
          href={t.href}
          replace
          className={cn(
            'flex h-9 items-center justify-center rounded-[9px] text-sm font-medium transition-colors',
            active === t.key ? 'bg-card text-fg shadow-sm' : 'text-fg-3 hover:text-fg-2',
          )}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}

export function FormError({ children }: { children: ReactNode }) {
  return <p className="animate-fade-in rounded-xl border border-loss/25 bg-loss-soft px-3.5 py-2.5 text-sm text-loss">{children}</p>;
}
